import {ensurePreviousActiveDialogIsClosed} from '@github-ui/diff-lines/ensure-previous-active-dialog-is-closed'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CommentDiscussionIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useRef, useState} from 'react'

import {CommentsSidePanel} from './CommentsSidePanel'
import type {PullRequest} from '../../page-data/payloads/toolbar'
import type {PageLimits} from '../../page-data/payloads/files'
import type {AliveChannels} from '../../page-data/payloads/header'
import {useIssueCommentsCount, useThreadCommentCount} from '../../stores/selectors'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {useDockedPanel, useDockedPanelActions} from '../../contexts/DockedPanelContext'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export interface OpenCommentsSidePanelButtonProps {
  isFileTreeExpanded?: boolean
  pageLimits: PageLimits
  pullRequest: PullRequest
  aliveChannels: AliveChannels
  tabSize?: number
}

/**
 * Renders a button that opens the comments side panel
 */
export function OpenCommentsSidePanelButton({
  isFileTreeExpanded,
  pageLimits,
  pullRequest,
  aliveChannels,
  tabSize,
}: OpenCommentsSidePanelButtonProps) {
  const isPrsDockedSidePanelEnabled = isFeatureEnabled('prs_docked_side_panel')
  const panelName = useDockedPanel()
  const commentsDockedSidePanelIsOpen = panelName === 'comments'

  const [commentsSidePanelIsOpen, setCommentsSidePanelIsOpen] = useState(false)
  const toggleSidePanelRef = useRef<HTMLButtonElement>(null)
  const actions = useDockedPanelActions()

  const diffCommentCount = useThreadCommentCount()
  const issueCommentsCount = useIssueCommentsCount()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  return (
    <div className="d-flex flex-items-center">
      <ErrorBoundary
        boundaryName="OpenCommentsSidePanelButton"
        fallback={
          <Button
            aria-label="The comments side panel cannot currently be opened."
            leadingVisual={CommentDiscussionIcon}
            size="small"
            variant="invisible"
          />
        }
      >
        <Button
          ref={toggleSidePanelRef}
          aria-label="Open comments side panel"
          count={diffCommentCount}
          leadingVisual={CommentDiscussionIcon}
          size="small"
          onClick={() => {
            sendPullRequestAnalyticsEvent('comments_side_panel.open', 'COMMENTS_SIDE_PANEL_BUTTON')
            if (isPrsDockedSidePanelEnabled) {
              actions.togglePanel('comments', toggleSidePanelRef.current)
            } else {
              ensurePreviousActiveDialogIsClosed()
              setCommentsSidePanelIsOpen(true)
            }
          }}
        >
          {isFileTreeExpanded && <span className="d-none d-xl-block">Comments</span>}
        </Button>
        <CommentsSidePanel
          isOpen={commentsSidePanelIsOpen || commentsDockedSidePanelIsOpen}
          pageLimits={pageLimits}
          pathName={pullRequest.pathName}
          pullRequestAuthorLogin={pullRequest.author.login}
          aliveChannels={aliveChannels}
          tabSize={tabSize}
          toggleSidesheetRef={toggleSidePanelRef}
          diffCommentCount={diffCommentCount}
          issueCommentsCount={issueCommentsCount}
          onClose={() => {
            if (isPrsDockedSidePanelEnabled) {
              actions.closePanel()
            } else {
              setCommentsSidePanelIsOpen(false)
            }
          }}
        />
      </ErrorBoundary>
    </div>
  )
}
